import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useFeatureFlag } from '~hooks';

import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, ClickableUploadIcon, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faDownload, faFileImport, faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view';

import { SahBudget } from '~support-at-home/models';

import columnsFactory from '~support-at-home/forms/sah-budgets/listAll';
import searchFields from '~support-at-home/forms/sah-budgets/search';

import { useGetProdaConnectionQuery } from '~support-at-home/actions/prodaConnection';
import { getAll, uploadFile } from '~support-at-home/actions/sahBudgets';

import { clear, clearError } from '~support-at-home/reducers/sahBudgets';

import {
  selectErrorResponse,
  selectLoading,
  selectSahBudgets,
  selectUploadError,
} from '~support-at-home/selectors/sahBudgets';

import AcoImportModal from './AcoImportModal';
import AddBudgetModal from './AddBudgetModal';
import SahBudgetsModal from './BudgetsModal';
import ImportBudgetsModal from './ImportBudgetsModal';

const Budgets: React.FC = () => {
  const dispatch = useAppDispatch();
  const budgets = useAppSelector(selectSahBudgets);
  const loading = useAppSelector(selectLoading) === 'pending';
  const errorResponse = useAppSelector(selectErrorResponse);
  const uploadError = useAppSelector(selectUploadError);

  const [isClientOpen, setClientOpen] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isAcoImportOpen, setAcoImportOpen] = useState(false);
  const [isImportOpen, setImportOpen] = useState(false);
  const [isImportMenuOpen, setImportMenuOpen] = useState(false);

  // Importing from Services Australia needs BOTH halves of the gate, and they are different things:
  // `feature-proda-direct` is per USER, the connection is per COMPANY. isResolved comes first or the
  // button flashes in for flagless users on first paint.
  //
  // The connection query is only ever issued once the flag is known-enabled, which is what makes it
  // usable here: its route is itself flag-gated server side, so a flagless user would 403 on it.
  // Without the tag `supportathome:View` the data is simply absent and the button stays hidden,
  // which is the right way to fail. Visibility only - the server is the authority on whether an
  // import can actually run, and names the cause in its response.
  const { isEnabled: isProdaEnabled, isResolved: isFlagResolved } = useFeatureFlag('feature-proda-direct');
  const { data: prodaConnection } = useGetProdaConnectionQuery(undefined, {
    skip: !isFlagResolved || !isProdaEnabled,
  });
  const showImportButton = isFlagResolved && isProdaEnabled && prodaConnection?.status === 'connected';

  const [data, setData] = useState<SahBudget[]>(budgets as SahBudget[]);
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('sah-client-budgets-search', {});
  const search = (values: { [key: string]: any }) => setSearchParams(values);

  const toggleModal = (toggle: boolean, clientId: number | null = null) => {
    setSelectedClientId(clientId);
    setClientOpen(toggle);
    load();
  };

  // toggleModal already reloads the list, which a close needs even on Cancel (a rollover inside the
  // modal changes the list without a save).
  const onModalClose = () => toggleModal(false);

  const openAddModal = () => setAddModalOpen(true);
  const closeAddModal = () => setAddModalOpen(false);
  const openAcoImportModal = () => setAcoImportOpen(true);
  const closeAcoImportModal = () => {
    setAcoImportOpen(false);
    load();
  };
  const openImportModal = () => setImportOpen(true);
  const closeImportModal = () => {
    setImportOpen(false);
    load();
  };

  const load = async () => {
    dispatch(clearError([]));
    dispatch(getAll({ ...searchParams }));
  };

  const onUpload = async (file: File, setProgress: (value: number) => void) => {
    try {
      await dispatch(uploadFile({ file, setProgress })).unwrap();
      await load();
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const columns = columnsFactory((id) => toggleModal(true, id));

  // From the clicked list row, not `selectClientByID`, since that depends on `main.clients.rows` being
  // loaded, which is not guaranteed here, and an API include would add a cross-DB query to every
  // modal open just to fetch a name already sitting on this row.
  const selectedClientName =
    (budgets as SahBudget[]).find((budget) => budget.client_id === selectedClientId)?.client?.full_name ?? null;

  useEffect(() => {
    load();
    return () => {
      dispatch(clear([]));
    };
  }, []);

  useEffect(() => {
    load();
  }, [searchParams]);

  useEffect(() => {
    setData(budgets as SahBudget[]);
  }, [budgets]);

  return (
    <div className="p-2">
      <ErrorResponse message={errorResponse} dataCy="sah-client-budgets-error" />
      <Toolbar
        id="config-budgets"
        rightCluster={
          <>
            <ClickableUploadIcon
              id="sah-client-budgets-file-upload-button"
              icon={faFileImport}
              toolTip="Import CSV / JSON File"
              onUpload={onUpload}
              externalError={uploadError}
            />
            <ClickableIcon
              id="sah-client-budgets-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Dropdown isOpen={isImportMenuOpen} toggle={() => setImportMenuOpen(!isImportMenuOpen)} direction="down">
              <DropdownToggle caret size="sm" color="success" data-cy="sah-budgets-import-menu-toggle">
                Import
              </DropdownToggle>
              <DropdownMenu>
                {/*
                  Gated on the individual item, not the toggle: a user without the PRODA gate still
                  needs the ACO import. See the showImportButton comment above for why both halves
                  of that gate are required.
                */}
                {showImportButton && (
                  <DropdownItem
                    id="sah-client-budgets-import-button"
                    data-cy="sah-budgets-import-proda-menuitem"
                    onClick={openImportModal}
                  >
                    <Icon className="me-2" icon={faDownload} />
                    Import from PRODA
                  </DropdownItem>
                )}
                <DropdownItem
                  id="sah-client-budgets-aco-upload-button"
                  data-cy="sah-budgets-import-aco-menuitem"
                  onClick={openAcoImportModal}
                >
                  <Icon className="me-2" icon={faFileImport} />
                  Import from ACO File
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <Button id="sah-client-budgets-add-button" size="sm" color="success" onClick={openAddModal}>
              <Icon className="me-2" icon={faPlus} />
              Add Budget
            </Button>
          </>
        }
      />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} onSubmit={search} />
      <TableView
        id="sah-client-budgets-table"
        columns={columns}
        data={data}
        emptyMessage={'No client SAH Budgets found'}
        // While the modal is open, any request in flight belongs to it, and the page table sits
        // behind its backdrop, so showing the page spinner there is a flicker with nothing to look at.
        isLoading={loading && !isClientOpen}
        readonly
      />
      <SahBudgetsModal
        id={selectedClientId}
        clientName={selectedClientName}
        isOpen={isClientOpen}
        onClose={onModalClose}
      />
      <AddBudgetModal isOpen={isAddModalOpen} onClose={closeAddModal} onSuccess={load} />
      <AcoImportModal isOpen={isAcoImportOpen} onClose={closeAcoImportModal} />
      <ImportBudgetsModal isOpen={isImportOpen} onClose={closeImportModal} />
    </div>
  );
};

export default Budgets;
